/* eslint-disable @typescript-eslint/no-empty-function */
/** @jsx jsx */
import { jsx } from '@emotion/react';
import { createContext, FC, useState, useContext, useEffect } from 'react';
import { InvoiceWebResponse } from '@/components/Invoice/types';
import { CreateQuoteResponse } from '@/types/automaticCurrencyConversion';
import { useCurrencySetting } from '@/hooks/useCurrencySetting';
import { useGetInvoice } from '@/hooks/useGetInvoice';
import { CollectionMethod } from '@/constants';
import CustomSpinner from '@/common/Spinner';
import { useGetCustomer } from '@/hooks/useGetCustomer';
import { Customer } from '@/api/customer';
import { airTracker } from '@/utils/airTracker';
import { COMPONENT_NAME } from '@/pages/Main';
import { usePerformanceMeasure } from '@/hooks/usePerformanceMeasure';
import { useCountryAddress } from '@/hooks/useCountryAddress';

type Store = {
  invoiceData: InvoiceWebResponse;
  accCurrencyList: string[];
  country?: string;
  quote?: CreateQuoteResponse;
  setQuote: (data?: CreateQuoteResponse) => void;
  signature: string;
  customer?: Customer;
};

export const StoreContext = createContext<Store>({
  invoiceData: {} as InvoiceWebResponse,
  accCurrencyList: [],
  country: undefined,
  quote: undefined,
  setQuote: () => {},
  signature: '',
  customer: undefined,
});

interface IStoreProviderProps {
  signature: string;
}
export const StoreProvider: FC<IStoreProviderProps> = ({ signature, children }) => {
  const [quote, setQuote] = useState<CreateQuoteResponse>();

  const { invoiceData, loading: invoiceDataLoading } = useGetInvoice(signature);
  const enabledPa = invoiceData?.invoice?.collectionMethod !== CollectionMethod.OUT_OF_BAND;
  const clientSecret = invoiceData?.secret;
  const customerId = invoiceData?.transaction?.customerExternalId;
  const { accCurrencyList, loading: accCurrencyListLoading } = useCurrencySetting(enabledPa, clientSecret);
  const { customer, loading: customerLoading } = useGetCustomer(enabledPa, clientSecret, customerId);
  const { country, loading: countryLoading } = useCountryAddress();
  const loading = invoiceDataLoading || accCurrencyListLoading || customerLoading || countryLoading;

  usePerformanceMeasure({
    name: 'CONTEXT_LOADING',
    ready: !loading,
  });

  if (loading) {
    return <CustomSpinner />;
  }
  return (
    <StoreContext.Provider
      value={{
        invoiceData: invoiceData || ({} as InvoiceWebResponse),
        accCurrencyList,
        country,
        quote,
        setQuote,
        signature,
        customer,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = (): Store => useContext(StoreContext);
